import { accountStore } from "@features/account/account-store"
import { currentSubscriptions } from "@features/account/memberships"
import { useAccount } from "@features/account/useAccount"
import { Button } from "@features/atoms/Button"
import { Skeleton } from "@features/atoms/Skeleton"
import { TextField } from "@features/atoms/TextField"
import { Spinner } from "@features/motion/Spinner"
import { cn } from "@utils/tw"
import dayjs from "dayjs"
import { Link } from "eddev/routing"
import { ComponentProps, FormEvent, ReactNode, useEffect, useState } from "react"
import { Spektrix } from "../../server/lib/spektrix-types"
import { useFlyingCarpet } from "./flying-carpet"

/** Spektrix's own Set Password page asks for at least 8 characters. */
const MIN_PASSWORD_LENGTH = 8

export function LoginCarpetPage() {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run } = useAccountAction()

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    void run(() => account.signIn({ email: String(form.get("email")), password: String(form.get("password")) }))
  }

  return (
    <AccountCarpetLayout
      title="Sign in to your account"
      intro={
        <>
          New to Tower Theater? <TextButton onClick={() => carpet.open("account/create")}>Create an account here</TextButton>
        </>
      }
    >
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit}>
        <TextField label="Email" name="email" type="email" autoComplete="email" required />
        <TextField label="Password" name="password" type="password" autoComplete="current-password" required />
        <AccountError error={error} />
        <div className="mt-3 flex items-center justify-between gap-4">
          <TextButton className="type-label-s" onClick={() => carpet.open("account/forgot-password")}>
            Forgot password
          </TextButton>
          <Button type="submit" variant="filled" className="px-5" loading={account.isPending}>
            Sign in
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

export function ForgotPasswordCarpetPage() {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run, isRunning } = useAccountAction()
  const [sentTo, setSentTo] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const email = String(new FormData(event.currentTarget).get("email")).trim()
    if (await run(() => account.requestPasswordReset(email))) setSentTo(email)
  }

  if (sentTo) {
    return (
      <AccountCarpetLayout
        title="Check your email"
        intro={
          <>
            If there's an account for <strong>{sentTo}</strong>, we've emailed it a link to choose a new password.
            Remember to check your junk folder.
          </>
        }
      >
        <div className="mt-7 flex items-center justify-between gap-4">
          <TextButton className="type-label-s" onClick={() => setSentTo(null)}>
            Try another email
          </TextButton>
          <Button variant="filled" className="px-5" onClick={() => carpet.open("account/login")}>
            Back to sign in
          </Button>
        </div>
      </AccountCarpetLayout>
    )
  }

  return (
    <AccountCarpetLayout
      title="Reset your password"
      intro={
        <>
          Enter the email for your account and we'll send you a link to choose a new password. Remembered it?{" "}
          <TextButton onClick={() => carpet.open("account/login")}>Sign in here</TextButton>
        </>
      }
    >
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit}>
        <TextField label="Email" name="email" type="email" autoComplete="email" required />
        <AccountError error={error} />
        <div className="mt-3 flex justify-end">
          <Button type="submit" variant="filled" className="px-5" loading={isRunning}>
            Send reset link
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

export function ResetPasswordCarpetPage() {
  const token = useAccount().passwordResetToken
  // Following another reset link starts over with its token.
  return <ResetPassword key={token} token={token} />
}

function ResetPassword({ token }: { token: string | null }) {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run, isRunning } = useAccountAction()
  const [status, setStatus] = useState<"checking" | "ready" | "invalid" | "done">(token ? "checking" : "invalid")
  const [mismatch, setMismatch] = useState(false)

  // Check the link before asking for a new password, so an expired link doesn't waste the customer's effort.
  useEffect(() => {
    if (!token) return
    let current = true
    accountStore.isPasswordResetTokenValid(token).then(
      (valid) => current && setStatus(valid ? "ready" : "invalid"),
      // If the check itself fails, let the reset report any problem with the link.
      () => current && setStatus("ready"),
    )
    return () => {
      current = false
    }
  }, [token])

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const password = String(form.get("password"))
    const isMatch = password === String(form.get("confirmPassword"))
    setMismatch(!isMatch)
    if (!token || !isMatch) return
    if (await run(() => account.resetPassword(token, password))) {
      setStatus("done")
      // Picks up the session if Spektrix signs the customer in after a reset.
      void account.refreshCustomer()
    }
  }

  if (status === "checking") {
    return (
      <div className="h-full flex items-center justify-center" role="status" aria-label="Checking your reset link">
        <Spinner className="text-[24px]" />
      </div>
    )
  }

  if (status === "invalid") {
    return (
      <AccountCarpetLayout
        title="This link has expired"
        intro="Password reset links can only be used once, and only for a limited time. Request a new link to choose a password."
      >
        <div className="mt-7 flex justify-end">
          <Button variant="filled" className="px-5" onClick={() => carpet.open("account/forgot-password")}>
            Send a new link
          </Button>
        </div>
      </AccountCarpetLayout>
    )
  }

  if (status === "done") {
    return (
      <AccountCarpetLayout title="Password updated" intro="Your new password is ready to use.">
        <div className="mt-7 flex justify-end">
          {account.customer ? (
            <Button variant="filled" className="px-5" onClick={() => carpet.open("account")}>
              Go to your account
            </Button>
          ) : (
            <Button variant="filled" className="px-5" onClick={() => carpet.open("account/login")}>
              Sign in
            </Button>
          )}
        </div>
      </AccountCarpetLayout>
    )
  }

  return (
    <AccountCarpetLayout title="Choose a new password" intro="Enter a new password for your Tower Theater account.">
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit}>
        <TextField
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <TextField
          label="Confirm new password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          error={mismatch ? "These passwords don't match." : undefined}
        />
        <AccountError error={error} />
        <div className="mt-3 flex justify-end">
          <Button type="submit" variant="filled" className="px-5" loading={isRunning}>
            Save password
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

export function CreateAccountCarpetPage() {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run } = useAccountAction()

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    void run(() =>
      account.createAccount({
        firstName: String(form.get("firstName")),
        lastName: String(form.get("lastName")),
        email: String(form.get("email")),
        password: String(form.get("password")),
      }),
    )
  }

  return (
    <AccountCarpetLayout
      title="Create an account"
      intro={
        <>
          Already have an account? <TextButton onClick={() => carpet.open("account/login")}>Sign in here</TextButton>
        </>
      }
    >
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First name" name="firstName" autoComplete="given-name" required />
          <TextField label="Last name" name="lastName" autoComplete="family-name" required />
        </div>
        <TextField label="Email" name="email" type="email" autoComplete="email" required />
        <TextField
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <AccountError error={error} />
        <div className="mt-3 flex justify-end">
          <Button type="submit" variant="filled" className="px-5" loading={account.isPending}>
            Create account
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

export function AccountCarpetPage() {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run } = useAccountAction()
  const customer = account.customer
  if (!customer) return null

  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ") || customer.name || customer.email

  return (
    <AccountCarpetLayout
      title={name}
      intro="Welcome back to Tower Theater."
      footer={
        <>
          <AccountError error={error} className="mb-3" />
          <Button variant="filled" className="w-full" loading={account.isPending} onClick={() => run(account.signOut)}>
            Sign out
          </Button>
        </>
      }
    >
      <MembershipStatus subscriptions={customer.subscriptions} />
      <div className="mt-8 flex flex-col gap-2">
        <Button variant="outline" className="w-full" onClick={() => carpet.open("account/tickets")}>
          Tickets
        </Button>
        <Button variant="outline" className="w-full" onClick={() => carpet.open("account/details")}>
          My information
        </Button>
        <Button variant="outline" className="w-full" onClick={() => carpet.open("account/change-password")}>
          Change password
        </Button>
      </div>
    </AccountCarpetLayout>
  )
}

export function DetailsCarpetPage() {
  const account = useAccount()
  const { error, run, isRunning } = useAccountAction()
  const [saved, setSaved] = useState(false)
  const customer = account.customer
  if (!customer) return null

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaved(false)
    const form = new FormData(event.currentTarget)
    const field = (name: string) => String(form.get(name) ?? "").trim()
    const details = {
      firstName: field("firstName"),
      lastName: field("lastName"),
      email: field("email"),
      mobile: field("mobile"),
      phone: field("phone"),
    }
    if (await run(() => account.updateDetails(details))) setSaved(true)
  }

  return (
    <AccountCarpetLayout title="My information" intro="These are the details we use for your bookings.">
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit} onChange={() => setSaved(false)}>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="First name"
            name="firstName"
            autoComplete="given-name"
            defaultValue={customer.firstName ?? ""}
            required
          />
          <TextField
            label="Last name"
            name="lastName"
            autoComplete="family-name"
            defaultValue={customer.lastName ?? ""}
            required
          />
        </div>
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          defaultValue={customer.email ?? ""}
          required
        />
        <TextField label="Mobile" name="mobile" type="tel" autoComplete="mobile tel" defaultValue={customer.mobile ?? ""} />
        <TextField label="Phone" name="phone" type="tel" autoComplete="home tel" defaultValue={customer.phone ?? ""} />
        <AccountError error={error} />
        {saved && (
          <p role="status" className="type-body-xs">
            Your details have been saved.
          </p>
        )}
        <div className="mt-3 flex justify-end">
          <Button type="submit" variant="filled" className="px-5" loading={isRunning}>
            Save changes
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

export function ChangePasswordCarpetPage() {
  const account = useAccount()
  const carpet = useFlyingCarpet()
  const { error, run, isRunning } = useAccountAction()
  const [mismatch, setMismatch] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const password = String(form.get("password"))
    const isMatch = password === String(form.get("confirmPassword"))
    setMismatch(!isMatch)
    if (!isMatch) return
    if (await run(() => account.changePassword(String(form.get("currentPassword")), password))) setDone(true)
  }

  if (done) {
    return (
      <AccountCarpetLayout title="Password changed" intro="Use your new password next time you sign in.">
        <div className="mt-7 flex justify-end">
          <Button variant="filled" className="px-5" onClick={() => carpet.open("account")}>
            Back to your account
          </Button>
        </div>
      </AccountCarpetLayout>
    )
  }

  return (
    <AccountCarpetLayout title="Change password">
      <form className="mt-7 flex flex-col gap-3" onSubmit={onSubmit}>
        <TextField
          label="Current password"
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          required
        />
        <TextField
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <TextField
          label="Confirm new password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          error={mismatch ? "These passwords don't match." : undefined}
        />
        <AccountError error={error} />
        <div className="mt-3 flex justify-end">
          <Button type="submit" variant="filled" className="px-5" loading={isRunning}>
            Change password
          </Button>
        </div>
      </form>
    </AccountCarpetLayout>
  )
}

function MembershipStatus(props: { subscriptions?: Spektrix.CustomerSubscription[] }) {
  const account = useAccount()
  const current = currentSubscriptions(props.subscriptions)
  const needsNames = current.some((subscription) => !subscription.membership?.name)

  useEffect(() => {
    if (needsNames) void account.loadMembershipNames()
  }, [needsNames])

  // Memberships haven't been read yet, so don't invite an existing member to join.
  if (!props.subscriptions) return null

  if (!current.length) {
    return (
      <Link href="/memberships" className="mt-4 inline-block type-label-s underline underline-offset-[3px]">
        Become a member
      </Link>
    )
  }

  return (
    <ul className="mt-4 flex flex-col gap-1 type-label-s text-blue-light">
      {current.map((subscription) => {
        const id = subscription.membership?.id
        const name = subscription.membership?.name || (id && account.membershipNames?.[id])
        return (
          <li key={`${id}:${subscription.expiryDate}`}>
            {name || (account.membershipNames ? "Membership" : <Skeleton>Cinephile Standard Pass</Skeleton>)} ·{" "}
            {subscription.autoRenew ? "Renews" : "Expires"} {dayjs(subscription.expiryDate).format("MMM D, YYYY")}
          </li>
        )
      })}
    </ul>
  )
}

export function AccountCarpetLayout(props: {
  title: ReactNode
  intro?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Auto margins centre the content, and let it scroll from the top when it overflows. */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-grid-margin md:px-4">
        <div className="my-auto py-4">
          <h2 className="type-title-m text-balance">{props.title}</h2>
          {props.intro ? <p className="mt-4 type-body-xs">{props.intro}</p> : null}
          {props.children}
        </div>
      </div>
      {props.footer ? (
        <div className="shrink-0 px-grid-margin md:px-4 pb-grid-margin md:pb-4">{props.footer}</div>
      ) : null}
    </div>
  )
}

/** An inline, underlined button for moving between account pages. */
function TextButton({ className, ...props }: ComponentProps<"button">) {
  return <button type="button" {...props} className={cn("underline underline-offset-[3px]", className)} />
}

export function AccountError(props: { error: string | null; className?: string }) {
  if (!props.error) return null
  return (
    <p role="alert" className={cn("type-body-xs text-error-dark", props.className)}>
      {props.error}
    </p>
  )
}

function useAccountAction() {
  const [error, setError] = useState<string | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  /** Runs an account action, showing its error message. Resolves to whether it succeeded. */
  async function run(action: () => Promise<void>) {
    setError(null)
    setIsRunning(true)
    try {
      await action()
      return true
    } catch (error) {
      setError(error instanceof Error ? error.message : "Something went wrong. Please try again.")
      return false
    } finally {
      setIsRunning(false)
    }
  }

  return { error, run, isRunning }
}
